import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of, catchError, tap, map } from 'rxjs';
import { environment } from 'src/environments/environment';
import { SocketService } from './socket.service';

/**
 * System status — the state of each subsystem, shown as a dot in the header.
 *
 * `tasks/system-status.md` §11.
 *
 * ── THIS IS A STATE, NOT AN EVENT ────────────────────────────────────────────────────
 *
 * Unlike `announcement.service.ts` there is nothing per-user here: no receipts, no dismissals, no
 * ordering rules. Every user on the platform gets the same answer, which is why the server can
 * cache it and why this service is only a cache of a cache.
 *
 * ── `unknown` IS WHY "GREEN BY DEFAULT" IS NOT QUITE RIGHT ───────────────────────────
 *
 * If the API is unreachable then the status endpoint is unreachable too. A dot that stays green
 * because it could not ask is worse than no dot at all — it asserts health at the exact moment
 * there is none. So a failed fetch, and an answer older than `STALE_AFTER_MS`, both resolve to
 * `unknown` (grey) rather than to `operational`.
 *
 * ── ...AND WHY `unknown` MUST HAVE A WAY BACK ────────────────────────────────────────
 *
 * The first version of this had no polling at all: one fetch on construction, then updates only
 * from the socket event. `lastSuccessAt` therefore only ever aged, so EVERY session went grey after
 * five idle minutes and stayed grey until a reload — `unknown` came to mean "we have not asked
 * recently" rather than "we asked and could not reach it", which is a different and much less
 * useful claim.
 *
 * `SystemStatusDotComponent` now drives a poll. It owns the timer rather than this service so that
 * it runs exactly while the dot is on screen and stops at logout, and so a background tab costs
 * nothing.
 *
 * The honest limit that follows: this reports MedicAI's view of MedicAI. It cannot see the user's
 * own network or their practice's internet, and the panel says so.
 */

export type SubsystemStatus = 'operational' | 'degraded' | 'down' | 'maintenance';
/** Not a stored state — what the client shows when it has no fresh answer. */
export type DisplayStatus = SubsystemStatus | 'unknown';

export interface Subsystem {
    key: string;
    label: string;
    description: string;
    audience: 'clinical' | 'practice';
    status: SubsystemStatus;
    /** The admin's line. Only present when the subsystem is not operational. */
    message: string | null;
    /** What to do instead. Only present when the subsystem is not operational. */
    whenDegraded: string | null;
    /** When the current state began — what makes staleness visible to the user. */
    since: string | null;
    autoClearAt: string | null;
    source: 'manual' | 'derived';
}

interface SystemStatusResponse {
    success: boolean;
    components: Subsystem[];
    overall: SubsystemStatus;
    checkedAt: string;
}

/**
 * How stale an answer may be before the dot drops to `unknown`.
 *
 * Deliberately generous against the server's ~30s cache: a dot that flickers grey on a slow
 * network is one people learn to ignore, and the failure this guards against — an API unreachable
 * for minutes — is not subtle.
 */
const STALE_AFTER_MS = 5 * 60 * 1000;

/** Severity order, worst first. Mirrors `utils/systemStatusConstants.js`. */
const SEVERITY: SubsystemStatus[] = ['down', 'degraded', 'maintenance', 'operational'];

@Injectable({
    providedIn: 'root',
})
export class SystemStatusService {

    private subsystemsSubject = new BehaviorSubject<Subsystem[]>([]);
    subsystems$: Observable<Subsystem[]> = this.subsystemsSubject.asObservable();

    /**
     * What the dot should show. `unknown` until the first successful fetch — never `operational`
     * on an assumption.
     */
    private overallSubject = new BehaviorSubject<DisplayStatus>('unknown');
    overall$: Observable<DisplayStatus> = this.overallSubject.asObservable();

    /** Emits each subsystem that has just become non-operational, for a one-off snackbar. */
    private degradedSubject = new BehaviorSubject<Subsystem | null>(null);
    justDegraded$: Observable<Subsystem | null> = this.degradedSubject.asObservable();

    /** When the server resolved the snapshot we are currently showing. */
    private checkedAtSubject = new BehaviorSubject<string | null>(null);
    checkedAt$: Observable<string | null> = this.checkedAtSubject.asObservable();

    private lastSuccessAt = 0;

    /** Milliseconds since the last successful answer, or `Infinity` if there has never been one. */
    get ageOfLastSuccess(): number {
        return this.lastSuccessAt === 0 ? Infinity : Date.now() - this.lastSuccessAt;
    }

    /** What the dot is currently showing. Read by the poller to decide how hard to retry. */
    get displayStatus(): DisplayStatus {
        return this.overallSubject.value;
    }
    /** Statuses at the previous poll, so a TRANSITION can be distinguished from a standing state. */
    private previous = new Map<string, SubsystemStatus>();

    constructor(
        private http: HttpClient,
        private socketService: SocketService,
    ) {
        this.load().subscribe();

        // Payload-free, like `announcementsChanged`: the client re-fetches rather than trusting a
        // pushed payload, so there is one rule for both fan-outs.
        this.socketService.listen('systemStatusChanged', 'system-status-service')
            .subscribe(() => this.load().subscribe());
    }

    private getAuthHeaders(): HttpHeaders {
        const token = localStorage.getItem('token');
        return new HttpHeaders().set('Authorization', `Bearer ${token}`);
    }

    load(): Observable<SystemStatusResponse | null> {
        return this.http.get<SystemStatusResponse>(
            `${environment.API_URL}/api/system-status`,
            { headers: this.getAuthHeaders() },
        ).pipe(
            catchError(() => {
                // Fail CLOSED to grey. Never leave a stale green sitting there — see the header.
                this.overallSubject.next(this.freshEnough() ? this.overallSubject.value : 'unknown');
                return of(null);
            }),
            tap((result) => {
                if (!result) return;
                this.lastSuccessAt = Date.now();
                this.emitTransitions(result.components || []);
                this.subsystemsSubject.next(result.components || []);
                this.overallSubject.next(result.overall || 'operational');
                this.checkedAtSubject.next(result.checkedAt || null);
            }),
        );
    }

    /** Whether the last successful answer is recent enough to keep showing. */
    private freshEnough(): boolean {
        return this.lastSuccessAt > 0 && (Date.now() - this.lastSuccessAt) < STALE_AFTER_MS;
    }

    /**
     * Recomputes the display status, dropping to `unknown` when the answer has gone stale.
     *
     * Called by the component on a timer rather than run here on an interval: a service polling in
     * the background would keep a timer alive for the life of the app to change a colour nobody
     * may be looking at.
     */
    refreshFreshness(): void {
        if (!this.freshEnough() && this.overallSubject.value !== 'unknown') {
            this.overallSubject.next('unknown');
        }
    }

    /**
     * Emits subsystems that have just become non-operational.
     *
     * A TRANSITION, not a state: something already amber when the app loaded is not news, and
     * announcing it on every reload is how a notification becomes wallpaper. The first poll only
     * populates the baseline, and so does the first sighting of a newly-deployed subsystem.
     */
    private emitTransitions(components: Subsystem[]): void {
        for (const component of components) {
            const before = this.previous.get(component.key);
            this.previous.set(component.key, component.status);

            // No previous value means either the FIRST poll of the session or a subsystem newly
            // added by a deploy. Both are the same case and both are correctly silent: something
            // already amber when we arrived is not news. A separate `firstPoll` guard stood here
            // and was redundant with this one — a mutation removing it survived, which is what
            // exposed it as dead.
            if (before === undefined) continue;
            if (before === component.status) continue;
            if (component.status === 'operational') continue;

            this.degradedSubject.next(component);
        }
    }

    /** The worst status in a set — the same ordering the server uses. */
    worst(components: Subsystem[]): SubsystemStatus {
        if (!components || !components.length) return 'operational';
        let worstIndex = SEVERITY.indexOf('operational');
        for (const component of components) {
            const index = SEVERITY.indexOf(component.status);
            // Fail closed on anything unrecognised, matching the server.
            if (index === -1) return SEVERITY[0];
            if (index < worstIndex) worstIndex = index;
        }
        return SEVERITY[worstIndex];
    }

    /** Subsystems that are not operational, worst first — what the panel highlights. */
    problems$: Observable<Subsystem[]> = this.subsystems$.pipe(
        map((components) => components
            .filter((component) => component.status !== 'operational')
            .sort((a, b) => SEVERITY.indexOf(a.status) - SEVERITY.indexOf(b.status))),
    );
}
